import { AbstractControl, FormArray, FormGroup } from '@angular/forms';

/**
 * Force every control in a form tree to re-run its own validators against its
 * CURRENT value, bottom-up.
 *
 * WHY THIS EXISTS
 * ---------------
 * A very common bug across our forms: a Save/Submit handler gates on
 * `form.invalid` and silently `return`s, and the button appears to "do nothing"
 * — yet a full page refresh makes the exact same data submit fine.
 *
 * The cause is a STALE validity status. When a form is populated
 * programmatically (`patchValue`/`reset`) and its fields are rendered through
 * custom ControlValueAccessors (date picker, phone, postcode, select, etc.),
 * a child accessor can write its value and leave the control's status as
 * INVALID even though the value is now valid. Crucially, calling
 * `updateValueAndValidity()` on the parent FormGroup does NOT fix this: on a
 * group it only re-aggregates the children's EXISTING statuses — it never asks
 * each child to re-validate. So the stale INVALID survives and blocks submit.
 * A full reload rebuilds the form from scratch, which is why "refresh fixes it".
 *
 * Calling this before the validity gate re-validates each leaf control against
 * its real value, so the gate reflects the true state.
 *
 * USAGE (drop into any submit handler before the invalid check):
 *   onSubmit(): void {
 *     this.form.markAllAsTouched();
 *     revalidateFormTree(this.form);
 *     if (this.form.invalid) { scrollToFirstInvalidControl(host); return; }
 *     ...emit / call API...
 *   }
 */
export function revalidateFormTree(control: AbstractControl | null | undefined): void {
  if (!control) {
    return;
  }

  if (control instanceof FormGroup) {
    for (const child of Object.values(control.controls)) {
      revalidateFormTree(child);
    }
    control.updateValueAndValidity({ onlySelf: true, emitEvent: false });
    return;
  }

  if (control instanceof FormArray) {
    for (const child of control.controls) {
      revalidateFormTree(child);
    }
    control.updateValueAndValidity({ onlySelf: true, emitEvent: false });
    return;
  }

  // Leaf FormControl: re-run its own validators against its current value.
  control.updateValueAndValidity({ onlySelf: true, emitEvent: false });
}

/**
 * Flush browser-autofilled values from the DOM into the reactive form.
 *
 * Chrome fires an `input` event when it autofills, so Angular's value accessor
 * updates the control. Firefox and Safari frequently DON'T: they set the input's
 * DOM value with no `input`/`change` event and no `:-webkit-autofill` animation.
 * If the user never focuses those fields (e.g. a browser fills email/name on
 * load), their controls stay empty even though the field visibly has text — so
 * the form is invalid and Save silently does nothing. A full reload can re-fill
 * differently, which is why it looks browser- and machine-specific.
 *
 * Call this at the very start of a submit handler (before validating). It finds
 * every input/textarea managed by AutofillSyncDirective (marked with the
 * `data-autofill-sync` attribute, so custom search/select inputs are left alone)
 * and re-dispatches the real `input` event the value accessor listens to —
 * synchronously, cross-browser.
 */
export function commitAutofilledValues(host: HTMLElement | null | undefined): void {
  if (!host || typeof host.querySelectorAll !== 'function') {
    return;
  }

  const fields = host.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>(
    '[data-autofill-sync]',
  );

  fields.forEach((field) => {
    if (field.readOnly || field.disabled) {
      return;
    }

    if (field instanceof HTMLInputElement && field.type === 'number') {
      return;
    }

    field.dispatchEvent(new Event('input', { bubbles: true }));
  });
}

/**
 * Scroll the first invalid field inside `host` into view so an invalid submit is
 * never a silent dead-end. Relies on our reusable inputs exposing invalidity as
 * `aria-invalid="true"` and/or a red border utility class. Deferred a tick so
 * Angular has painted the error state before we query the DOM.
 */
export function scrollToFirstInvalidControl(host: HTMLElement | null | undefined): void {
  if (!host || typeof host.querySelector !== 'function') {
    return;
  }

  setTimeout(() => {
    const firstInvalid = host.querySelector<HTMLElement>(
      '[aria-invalid="true"], .border-red500, .border-red600, .border-red500\\/60',
    );
    firstInvalid?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  });
}
